import $ from 'jquery';
import { FilterService } from '@bingads-webui/filter-service';
import { getGridPreferences } from '@bingads-webui-campaign/summary-grid-helper';
import { Hotel, HotelGroup, SubAccount, HotelDimensionRow } from '@bingads-webui/hotel-odata-schemas';
import { columnChooserPlugin } from '@bingads-webui-campaign/projection-grid-column-chooser';
import { WaterfallFilterStorage, WaterfallFilterType, waterfallDateFilterProcessor } from '@bingads-webui-campaign/filter-storage';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';

/* eslint-disable import/extensions,import/no-unresolved, */
import * as preferenceStoreFactory from '@bingads-webui-component-legacy/bingads-grid/service/filter-preferences';
import { HotelCenterOverview as HotelCenterOverviewPromise, userCountryService } from '@bingads-webui-campaign-legacy/hotel-center-overview/overview-ui-next';
import appConfig from '@bingads-webui-component-legacy/config/index';
import appState from '@bingads-webui-component-legacy/state/index';
import dataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';
import jsData from '@bingads-webui-component-legacy/js-data-campaign/index';
import sessionStore from '@bingads-webui-component-legacy/js-data-storage/session-storage';
import { fetchPaginationConfig } from '@bingads-webui-component-legacy/bingads-grid/service/fetch-pagination';
import preferenceServiceLegacy from '@bingads-webui-component-legacy/preference/service/index';
/* eslint-enable */

import { getFilterConfig } from './filter-config';
import { extendPlugins } from './extend-plugins';

const getGridSchemaByName = (gridName) => {
  let schema = null;
  switch (gridName) {
    case 'HotelIdGrid':
      schema = Hotel;
      break;
    case 'HotelGroupGrid':
      schema = HotelGroup;
      break;
    case 'HotelSubAccountGrid':
      schema = SubAccount;
      break;
    default:
      schema = HotelDimensionRow;
      break;
  }
  return schema;
};

const getGridNavigationLevelsByName = (gridName) => {
  let navigationLevels = [];
  switch (gridName) {
    case 'HotelIdGrid':
      navigationLevels = [{ name: 'grid-chooser-item-hotel-id' }];
      break;
    case 'HotelGroupGrid':
      navigationLevels = [{ name: 'grid-chooser-item-hotel-group' }];
      break;
    case 'HotelSubAccountGrid':
      navigationLevels = [{ name: 'grid-chooser-item-subaccount' }];
      break;
    case 'HotelDeviceTypeGrid':
      navigationLevels = [{ name: 'grid-chooser-item-devicetype' }];
      break;
    case 'HotelDateGrid':
      navigationLevels = [{ name: 'grid-chooser-item-date' }];
      break;
    case 'HotelLengthOfStayGrid':
      navigationLevels = [{ name: 'grid-chooser-item-length-of-stay' }];
      break;
    case 'HotelUserCountryGrid':
      navigationLevels = [{ name: 'grid-chooser-item-user-country' }];
      break;
    case 'HotelSlotTypeGrid':
      navigationLevels = [{ name: 'grid-chooser-item-slot-type' }];
      break;
    case 'HotelCountryGrid':
      navigationLevels = [{ name: 'grid-chooser-item-hotel-country' }];
      break;
    case 'AdvancedBookingGrid':
      navigationLevels = [{ name: 'grid-chooser-item-advanced-booking' }];
      break;
    case 'CheckinDayGrid':
      navigationLevels = [{ name: 'grid-chooser-item-checkin-day' }];
      break;
    case 'DateTypeGrid':
      navigationLevels = [{ name: 'grid-chooser-item-date-type' }];
      break;
    case 'SiteTypeGrid':
      navigationLevels = [{ name: 'grid-chooser-item-site-type' }];
      break;
    default:
      navigationLevels = [];
      break;
  }
  return navigationLevels;
};

const loadOverview = () => HotelCenterOverviewPromise;

export const createOverviewGrid = ({
  serverContext,
  scope,
  permissions,
  preferenceService,
  i18n,
  toolbarPlugin,
  crossPageSelectPlugin,
  gridName,
  updateSelectedSubaccount,
  refreshGridForUINext,
  jumpToReconReports,
  perfMarker,
  gridRefreshed,
}) =>
  loadOverview({
    serverContext,
    scope,
    permissions,
    preferenceService,
  }).then((HotelCenterOverviewObj) => {
    const [{ HotelCenterOverview }, ...bulkItems] = HotelCenterOverviewObj;
    const legacyGlobalI18n = window.globalLegacyI18n.get('$/i18n!legacy/global');
    const hotelCenterI18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center');
    const schemaI18nCampaign = window.globalLegacyI18n.get('$/schema-i18n!campaign');
    const filterConfig = getFilterConfig({
      entitySchema: getGridSchemaByName(gridName),
      i18n: hotelCenterI18n,
      sessionStore,
      preferenceStoreFactory: preferenceStoreFactory.getFilters,
      legacyGlobalI18n,
      schemaI18nCampaign,
      dataContextManager,
      preferenceService,
      appConfig,
      jsData,
      permissions,
      gridName,
    });
    const filterService = new FilterService(filterConfig);
    const waterfallFilterStorage = new WaterfallFilterStorage({
      preferenceService,
    });
    const getGridPreferencesPromise = getGridPreferences({
      gridName,
      dateUtil: {
        format: value => i18n.formatDate(value),
      },
      state: appState,
      fetchPaginationConfig,
    });
    const p$daterange = () => {
      const dateFilter = waterfallDateFilterProcessor(
        waterfallFilterStorage.getFilters([WaterfallFilterType.DateFilter]),
        serverContext
      );
      return Promise.resolve(dateFilter);
    };
    return getGridPreferencesPromise.then((preferenceConfig) => {
      userCountryService.getCountriesAndSetState();
      const injectedView = new HotelCenterOverview({
        i18n: mergeI18n(hotelCenterI18n, i18n),
        isUINext: true,
        serverContext,
        fetchPreferenceConfig: () => Promise.resolve(preferenceConfig),
        preferenceConfig,
        preferenceService: preferenceServiceLegacy,
        extendPlugins: extendPlugins({
          serverContext,
          toolbarPlugin,
          crossPageSelectPlugin,
          hotelCenterI18n,
          preferenceService: preferenceServiceLegacy,
          appConfig,
          filterService,
          BulkEditBidPanel: bulkItems[0].default,
          BulkEditBidMultiPanel: bulkItems[1].default,
          BulkIncreaseDecreaseBidPanel: bulkItems[2].default,
          gridName,
          appState,
          p$daterange,
        }),
        filterService,
        pluginFactories: {
          columnChooserPlugin,
        },
        updateSelectedSubaccount,
        gridName,
        p$daterange,
        perfMarker,
        refreshGridForUINext,
        jumpToReconReports,
        gridRefreshed,
        viewport: $('.hotel-center-overview-page'),
        customHeaderOffset: () => $('.sticky-container').height(),
        navigationLevels: getGridNavigationLevelsByName(gridName),
        notificationApi,
      });
      return {
        filterConfig, filterService, injectedView, hotelCenterI18n,
      };
    });
  });
